import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { fitSlots, SHEET_FRAME } from "@/lib/layout";
import { PhotosStep } from "@/components/editor/PhotosStep";
import { DetailsStep } from "@/components/editor/DetailsStep";
import { LayoutStep } from "@/components/editor/LayoutStep";

const EMPTY = { name: "", code: "", description: "", materials: "", highlights: [], show_writeup: true, configurations: [], images: [], slots: [], source_url: "" };
const STEPS = ["Photos", "Details & pricing", "Layout & preview"];

export default function ProductEditor() {
  const { id, pid } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [cat, setCat] = useState(null);
  const [layout, setLayout] = useState(null);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const prod = pid ? (await api.get(`/products/${pid}`)).data
          : { ...EMPTY, category_id: id, code: (await api.get(`/categories/${id}/next-code`)).data.code };
        setP(prod);
        const c = (await api.get(`/categories/${prod.category_id}`)).data;
        const { data: lay } = await api.get("/layouts");
        setLayout(lay.layouts.find((l) => l.id === c.layout_id) || lay.layouts[0]);
        setCat(c);
      } catch (e) { toast.error(errMsg(e)); }
    })();
  }, [id, pid]);

  const update = (patch) => setP((x) => ({ ...x, ...patch }));

  const goto = (n) => {
    if (n === 2) update({ slots: fitSlots(p.slots, frames, p.images) });
    setStep(n);
  };

  const canNext = step === 0 ? p?.images.length > 0 : step === 1 ? p?.name.trim() : true;

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...p, configurations: p.configurations.map((c) => ({ ...c, price_select: c.price_select === "" ? null : c.price_select, price_luxe: c.price_luxe === "" ? null : c.price_luxe })) };
      if (pid) await api.put(`/products/${pid}`, body); else await api.post("/products", body);
      toast.success(pid ? "Pages updated in the catalog" : `2 pages added to the ${cat.name} catalog`);
      nav(`/category/${p.category_id}`);
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };

  if (!p || !cat || !layout) return <p className="text-sm text-[var(--muted-ink)]">Loading…</p>;
  const frames = cat.style === "bespoke" ? [...layout.slots, SHEET_FRAME] : layout.slots;

  return (
    <div className="pb-28">
      <Link to={`/category/${cat.id}`} className="text-[11px] tracking-[0.25em] uppercase text-[var(--muted-ink)] hover:text-[var(--ink)]" data-testid="back-to-category">← {cat.name}</Link>
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mt-4 rise" data-testid="editor-title">{pid ? `Edit ${p.name}` : "Add a product"}</h1>
      <div className="flex gap-0 mt-10 mb-10 border-b border-[var(--line)] overflow-x-auto">
        {STEPS.map((s, i) => (
          <button key={s} onClick={() => (i <= step || canNext) && goto(i)} data-testid={`step-tab-${i}`}
            className={`px-1 mr-8 pb-3 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${step === i ? "border-[var(--terracotta)] text-[var(--ink)] font-semibold" : "border-transparent text-[var(--muted-ink)]"}`}>
            <span className="text-xs mr-2">0{i + 1}</span>{s}
          </button>
        ))}
      </div>
      {step === 0 && <PhotosStep p={p} update={update} cat={cat} />}
      {step === 1 && <DetailsStep p={p} update={update} cat={cat} />}
      {step === 2 && <LayoutStep p={p} update={update} cat={cat} frames={frames} layoutName={layout.name} />}

      <div className="fixed bottom-0 inset-x-0 z-30 bg-[var(--cream)]/90 backdrop-blur-md border-t border-[var(--line)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between gap-4">
          <p className="text-xs text-[var(--muted-ink)] hidden sm:block">{p.images.length} photos · {cat.style === "bespoke" ? (() => { const n = Object.values(p.bespoke?.prices || {}).filter((v) => v != null).length; return `${n} ${n === 1 ? "price" : "prices"}`; })() : `${p.configurations.length} configurations`}</p>
          <div className="flex gap-2 ml-auto">
            {step > 0 && <Button variant="outline" onClick={() => goto(step - 1)} data-testid="editor-back-button">Back</Button>}
            {step < 2 && <Button disabled={!canNext} onClick={() => goto(step + 1)} data-testid="editor-next-button">Continue</Button>}
            {step === 2 && (
              <Button disabled={saving || !p.name.trim()} onClick={save} className="bg-[var(--terracotta)] hover:bg-[#8d4427]" data-testid="save-product-button">
                {saving && <Loader2 size={14} className="mr-2 animate-spin" />}{pid ? "Save changes" : "Add 2 pages to catalog"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
